import React, { createContext, useContext, useState } from "react";
import { UserRole, UserSession } from "../types/auth.types";
import { clearOwnerAccessToken } from "../core/auth/ownerAccessToken";


interface AuthContextType {
  user: UserSession | null;
  login: (name: string, role: UserRole, userId: string, firmId?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_STORAGE_KEY = "caseflow_user_session";

const readPersistedSession = (): UserSession | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<UserSession>;
    if (
      typeof parsed.id !== "string" ||
      typeof parsed.name !== "string" ||
      typeof parsed.role !== "string"
    ) {
      return null;
    }

    if (!["ADVISER", "ASSISTANT", "CLIENT", "OWNER"].includes(parsed.role)) {
      return null;
    }

    return {
      id: parsed.id,
      name: parsed.name,
      role: parsed.role as UserRole,
      firmId: typeof parsed.firmId === "string" ? parsed.firmId : undefined,
    };
  } catch {
    return null;
  }
};

const persistSession = (session: UserSession | null) => {
  if (typeof window === "undefined") return;

  if (!session) {
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserSession | null>(() => readPersistedSession());

  const login = (name: string, role: UserRole, userId: string, firmId?: string) => {
    const normalizedUserId = userId.trim();
    if (!normalizedUserId) {
      throw new Error("Cannot create session without backend user ID.");
    }

    const session: UserSession = {
      id: normalizedUserId,
      name,
      role,
      firmId: firmId?.trim() || undefined,
    };
    persistSession(session);
    setUser(session);
  };

  const logout = () => {
    clearOwnerAccessToken();
    persistSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
